import { useContext } from "react";
import { RadioGroupContext } from "./RadioGroupContext";
import { useErrorDescribedBy } from "@/hooks/useFormControl";

type PropsWithValue<T> = T & {
  value?: string | number;
  "aria-describedby"?: string;
};

export const useRadioProps = <T extends object>(props: PropsWithValue<T>) => {
  const context = useContext(RadioGroupContext);
  // `aria-invalid` is not supported on a radio, so `RadioGroup` marks the
  // radiogroup. The message still has to be reachable from each option,
  // because the validation layer moves focus to one of them.
  const describedBy = useErrorDescribedBy(props["aria-describedby"]);
  const errorProps = describedBy ? { "aria-describedby": describedBy } : {};

  if (!context) {
    return { ...props, ...errorProps };
  }

  const { name, value, defaultValue, onChange } = context;

  return {
    ...props,
    ...errorProps,
    onChange,
    name,
    ...(typeof value !== "undefined" && { checked: props.value === value }),
    ...(typeof defaultValue !== "undefined" && {
      defaultChecked: props.value === defaultValue,
    }),
  };
};
